import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { DashboardCustomFilterComponent } from '../dashboard-custom-filter/dashboard-custom-filter.component';
import { CommonModule } from '@angular/common';

export interface QueueFilterItem {
    id: string;
    name: string;
    count: number;
    checked: boolean;
}

@Component({
    selector: 'app-queue-filter-carousel',
    templateUrl: './queue-filter-carousel.component.html',
    styleUrl: './queue-filter-carousel.component.scss',
    imports: [CommonModule, DashboardCustomFilterComponent]
})
export class QueueFilterCarouselComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() filters: QueueFilterItem[] = [];
    @Input() allCasesId: string;
    @Input() selectedCount: number = 0;
    @Input() isSideNavHidden: boolean = false;
    @Output() valueEmitted = new EventEmitter<string>();

    @ViewChild('carouselContainer', { static: false }) carouselContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('filtersViewport', { static: false }) filtersViewport!: ElementRef<HTMLDivElement>;

    isOverflowing = false;
    currentItemOffset = 0;
    private resizeObserver?: ResizeObserver;
    private isDragging = false;
    private dragStartX = 0;
    private dragStartScroll = 0;
    private readonly itemWidth = 200;
    private readonly itemGap = 10;
    private readonly arrowButtonWidth = 24;
    private readonly containerGap = 8;
    private readonly sidebarWidthWithNav = 400;
    private readonly sidebarWidthWithoutNav = 160;
    private readonly edgeTolerance = 1;
    private readonly onWindowResize = () => {
        this.calculateOverflow();
        this.clampOffset();
        this.syncScrollInstant();
    };

    private readonly onViewportScroll = () => {
        this.currentItemOffset = this.filtersViewport?.nativeElement?.scrollLeft ?? 0;
    };

    private readonly onMouseDown = (e: MouseEvent) => {
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartScroll = this.filtersViewport?.nativeElement?.scrollLeft ?? 0;

        const viewport = this.filtersViewport?.nativeElement;
        if (viewport) {
            viewport.style.cursor = 'grabbing';
            viewport.style.userSelect = 'none';
        }
    };

    private readonly onMouseMove = (e: MouseEvent) => {
        if (!this.isDragging) {
            return;
        }

        const delta = this.dragStartX - e.clientX;
        const newScrollLeft = this.dragStartScroll + delta;
        this.currentItemOffset = newScrollLeft;

        const viewport = this.filtersViewport?.nativeElement;
        if (viewport) {
            viewport.scrollLeft = newScrollLeft;
        }
    };

    private readonly onMouseUp = () => {
        this.isDragging = false;

        const viewport = this.filtersViewport?.nativeElement;
        if (viewport) {
            viewport.style.cursor = 'grab';
            viewport.style.userSelect = 'auto';
        }
    };

    private readonly onMouseLeave = () => {
        if (this.isDragging) {
            this.isDragging = false;

            const viewport = this.filtersViewport?.nativeElement;
            if (viewport) {
                viewport.style.cursor = 'grab';
                viewport.style.userSelect = 'auto';
            }
        }
    };

    ngOnChanges(changes: SimpleChanges): void {
        const filtersCountChanged =
            !!changes['filters'] &&
            (changes['filters'].previousValue?.length ?? 0) !== (changes['filters'].currentValue?.length ?? 0);

        if (filtersCountChanged) {
            this.currentItemOffset = 0;
        }

        if (filtersCountChanged || changes['isSideNavHidden']) {
            setTimeout(() => {
                this.calculateOverflow();
                this.clampOffset();
                this.syncScrollInstant();
            });
        }
    }

    ngAfterViewInit(): void {
        this.calculateOverflow();
        this.resizeObserver = new ResizeObserver(() => {
            this.calculateOverflow();
            this.clampOffset();
            this.syncScrollInstant();
        });
        window.addEventListener('resize', this.onWindowResize);
        if (this.carouselContainer?.nativeElement) {
            this.resizeObserver.observe(this.carouselContainer.nativeElement);
        }
        if (this.filtersViewport?.nativeElement) {
            this.resizeObserver.observe(this.filtersViewport.nativeElement);
            this.filtersViewport.nativeElement.addEventListener('scroll', this.onViewportScroll);
            this.filtersViewport.nativeElement.addEventListener('mousedown', this.onMouseDown);
            this.filtersViewport.nativeElement.addEventListener('mousemove', this.onMouseMove);
            this.filtersViewport.nativeElement.addEventListener('mouseup', this.onMouseUp);
            this.filtersViewport.nativeElement.addEventListener('mouseleave', this.onMouseLeave);
            this.filtersViewport.nativeElement.style.cursor = 'grab';
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.onWindowResize);
        const viewport = this.filtersViewport?.nativeElement;
        if (viewport) {
            viewport.removeEventListener('scroll', this.onViewportScroll);
            viewport.removeEventListener('mousedown', this.onMouseDown);
            viewport.removeEventListener('mousemove', this.onMouseMove);
            viewport.removeEventListener('mouseup', this.onMouseUp);
            viewport.removeEventListener('mouseleave', this.onMouseLeave);
        }
    }

    get itemStepWidth(): number {
        return this.itemWidth + this.itemGap;
    }

    get maxOffset(): number {
        const viewport = this.filtersViewport?.nativeElement;
        if (viewport) {
            return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        }

        if (!this.filters.length) {
            return 0;
        }

        const availableWidth = this.getAvailableWidth(true);
        const totalItemsWidth =
            this.filters.length * this.itemWidth + (this.filters.length - 1) * this.itemGap;

        return Math.max(0, totalItemsWidth - availableWidth);
    }

    get canScrollLeft(): boolean {
        return this.currentItemOffset > this.edgeTolerance;
    }

    get canScrollRight(): boolean {
        return this.currentItemOffset < (this.maxOffset - this.edgeTolerance);
    }

    private calculateOverflow(): void {
        if (!this.filters.length) {
            this.isOverflowing = false;
            this.currentItemOffset = 0;
            return;
        }

        const viewport = this.filtersViewport?.nativeElement;
        if (viewport) {
            this.isOverflowing = viewport.scrollWidth > viewport.clientWidth;
            return;
        }

        // Fallback when DOM not yet available
        const availableWidth = this.getAvailableWidth(false);
        const totalItemsWidth =
            this.filters.length * this.itemWidth + (this.filters.length - 1) * this.itemGap;

        this.isOverflowing = totalItemsWidth > availableWidth;
    }

    scrollItemLeft(): void {
        if (!this.canScrollLeft) {
            return;
        }

        this.currentItemOffset = Math.max(0, this.currentItemOffset - this.itemStepWidth);
        this.clampOffset();
        this.filtersViewport?.nativeElement?.scrollTo({ left: this.currentItemOffset, behavior: 'smooth' });
    }

    scrollItemRight(): void {
        if (!this.canScrollRight) {
            return;
        }

        this.currentItemOffset = Math.min(this.maxOffset, this.currentItemOffset + this.itemStepWidth);
        this.clampOffset();
        this.filtersViewport?.nativeElement?.scrollTo({ left: this.currentItemOffset, behavior: 'smooth' });
    }

    private clampOffset(): void {
        const max = this.maxOffset;

        if (this.currentItemOffset < this.edgeTolerance) {
            this.currentItemOffset = 0;
            return;
        }

        if ((max - this.currentItemOffset) < this.edgeTolerance) {
            this.currentItemOffset = max;
            return;
        }

        this.currentItemOffset = Math.min(this.currentItemOffset, max);
    }

    private syncScrollInstant(): void {
        if (this.filtersViewport?.nativeElement) {
            this.filtersViewport.nativeElement.scrollLeft = this.currentItemOffset;
        }
    }

    private getAvailableWidth(showArrows: boolean): number {
        const viewportWidth = this.filtersViewport?.nativeElement?.clientWidth ?? 0;
        if (viewportWidth > 0) {
            return viewportWidth;
        }

        const sidebarWidth = this.isSideNavHidden ? this.sidebarWidthWithoutNav : this.sidebarWidthWithNav;
        const containerWidth = window.innerWidth - sidebarWidth;
        const arrowsWidth = showArrows ? 2 * this.arrowButtonWidth : 0;
        const gapsWidth = showArrows ? 3 * this.containerGap : 0;
        return Math.max(0, containerWidth - arrowsWidth - gapsWidth);
    }

    onMouseWheel(event: WheelEvent): void {
        if (!this.isOverflowing) return;

        event.preventDefault();
        this.filtersViewport?.nativeElement.scrollBy({
            left: event.deltaX || event.deltaY,
            behavior: 'auto',
        });
    }

    onFilterClick(value: string): void {
        this.valueEmitted.emit(value);
    }
}
